/**
 * UserDetailPage — страница нагрузки сотрудника (/users/:id).
 *
 * Директор/зам видит:
 *  - Для ГИПа: список проектов которыми руководит (цвет, счётчики разделов)
 *  - Для проектировщика: список активных разделов (код, статус, срок, проект)
 * Клик на проект/раздел → /projects/:id
 *
 * Директор/зам может редактировать ФИО любого сотрудника (inline).
 */
import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { usersApi } from '@/api/users'
import { useEffectiveRole } from '@/store/auth'
import Spinner from '@/components/common/Spinner'
import { format } from 'date-fns'
import { ru } from 'date-fns/locale'
import { ArrowLeft, Briefcase, Layers, AlertCircle, CheckCircle2, Pencil, Check, X } from 'lucide-react'
import type { ProjectColor, SectionStatus } from '@/types'

const ROLE_LABELS: Record<string, string> = {
  director:        'Директор',
  deputy_director: 'Зам. директора',
  chief_engineer:  'Главный инженер проекта (ГИП)',
  designer:        'Проектировщик',
}

const STATUS_LABELS: Record<SectionStatus, string> = {
  not_started: 'Не начат',
  in_progress: 'В работе',
  on_review:   'На проверке',
  suspended:   'Приостановлен',
  issued:      'Выдан',
  accepted:    'Принят',
}

const STATUS_COLOR: Record<SectionStatus, string> = {
  not_started: 'bg-gray-100 text-gray-500',
  in_progress: 'bg-blue-100 text-blue-700',
  on_review:   'bg-purple-100 text-purple-700',
  suspended:   'bg-amber-100 text-amber-700',
  issued:      'bg-teal-100 text-teal-700',
  accepted:    'bg-emerald-100 text-emerald-700',
}

const COLOR_DOT: Record<ProjectColor, string> = {
  green:  'bg-emerald-500',
  yellow: 'bg-amber-400',
  red:    'bg-rose-500',
  grey:   'bg-gray-400',
}

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '-'
  return format(new Date(iso), 'dd.MM.yyyy', { locale: ru })
}

export default function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const effectiveRole = useEffectiveRole()
  const qc = useQueryClient()

  const canEditName = effectiveRole === 'director' || effectiveRole === 'deputy_director'
  const [editingName, setEditingName] = useState(false)
  const [nameValue, setNameValue] = useState('')

  const { data, isLoading, error } = useQuery({
    queryKey: ['user-workload', id],
    queryFn: () => usersApi.workload(id!).then((r) => r.data),
    enabled: !!id,
  })

  const nameMutation = useMutation({
    mutationFn: (full_name: string) => usersApi.patch(id!, { full_name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-workload', id] })
      qc.invalidateQueries({ queryKey: ['users'] })
      setEditingName(false)
    },
  })

  if (isLoading) return <Spinner size="lg" />
  if (error || !data) {
    return (
      <div className="p-6 text-rose-600 flex items-center gap-2">
        <AlertCircle className="h-5 w-5" />
        Не удалось загрузить данные сотрудника
      </div>
    )
  }

  const { user, gip_projects, assigned_sections } = data
  const isGip = user.role === 'chief_engineer'
  const isDesigner = user.role === 'designer'

  return (
    <div className="p-6 space-y-6 max-w-4xl">
      {/* Навигация */}
      <button
        onClick={() => navigate('/users')}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 transition-colors"
      >
        <ArrowLeft className="h-4 w-4" />
        Сотрудники
      </button>

      {/* Шапка */}
      <div className="rounded-xl bg-white border border-gray-200 shadow-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            {/* Имя с inline-редактированием */}
            <div className="flex items-center gap-2">
              {editingName ? (
                <>
                  <input
                    autoFocus
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') nameMutation.mutate(nameValue)
                      if (e.key === 'Escape') setEditingName(false)
                    }}
                    className="rounded-lg border border-brand-400 px-2 py-1 text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-brand-500 min-w-[220px]"
                  />
                  <button
                    onClick={() => nameMutation.mutate(nameValue)}
                    disabled={nameMutation.isPending}
                    className="rounded-lg p-1 text-emerald-600 hover:bg-emerald-50 transition-colors disabled:opacity-50"
                    title="Сохранить"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    className="rounded-lg p-1 text-gray-400 hover:bg-gray-100 transition-colors"
                    title="Отмена"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </>
              ) : (
                <>
                  <h1 className="text-xl font-semibold text-gray-900">
                    {user.full_name ?? <span className="italic text-gray-400">Имя не заполнено</span>}
                  </h1>
                  {canEditName && (
                    <button
                      onClick={() => { setNameValue(user.full_name ?? ''); setEditingName(true) }}
                      className="rounded-lg p-1 text-gray-300 hover:text-brand-600 hover:bg-brand-50 transition-colors"
                      title="Изменить ФИО"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                  )}
                </>
              )}
            </div>

            <div className="mt-1 flex flex-wrap items-center gap-3">
              <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                {ROLE_LABELS[user.role] ?? user.role}
              </span>
              <span className="text-sm text-gray-500">{user.email}</span>
              {!user.is_active && (
                <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">
                  Заблокирован
                </span>
              )}
            </div>
          </div>

          {/* Сводка */}
          <div className="flex gap-4">
            {isGip && (
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{gip_projects.length}</div>
                <div className="text-xs text-gray-500 mt-0.5">проектов</div>
              </div>
            )}
            {isDesigner && (
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{assigned_sections.length}</div>
                <div className="text-xs text-gray-500 mt-0.5">разделов</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Проекты ГИПа */}
      {isGip && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Briefcase className="h-4 w-4 text-brand-500" />
            Проекты под руководством
            <span className="ml-1 rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-600 font-medium">
              {gip_projects.length}
            </span>
          </h2>

          {gip_projects.length === 0 ? (
            <div className="rounded-xl bg-white border border-gray-200 shadow-sm px-5 py-8 text-center text-sm text-gray-400">
              Нет активных проектов в роли ГИПа
            </div>
          ) : (
            <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Проект</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Срок</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-500">Разделов</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-500">Просрочено</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {gip_projects.map((p) => (
                    <tr
                      key={p.id}
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/projects/${p.id}`)}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className={`inline-block h-2.5 w-2.5 rounded-full flex-shrink-0 ${COLOR_DOT[p.color]}`}
                          />
                          <div>
                            <span className="font-mono text-xs text-gray-400 mr-2">{p.code}</span>
                            <span className="font-medium text-gray-900">{p.name}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-600">{fmtDate(p.planned_end_date)}</td>
                      <td className="px-4 py-3 text-center text-gray-700">{p.section_count}</td>
                      <td className="px-4 py-3 text-center">
                        {p.overdue_count > 0 ? (
                          <span className="inline-flex items-center gap-1 text-rose-600 font-medium text-xs">
                            <AlertCircle className="h-3.5 w-3.5" />
                            {p.overdue_count}
                          </span>
                        ) : (
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Разделы проектировщика */}
      {isDesigner && (
        <section className="space-y-2">
          <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-700">
            <Layers className="h-4 w-4 text-brand-500" />
            Активные разделы
            <span className="ml-1 rounded-full bg-brand-100 px-2 py-0.5 text-xs text-brand-600 font-medium">
              {assigned_sections.length}
            </span>
          </h2>

          {assigned_sections.length === 0 ? (
            <div className="rounded-xl bg-white border border-gray-200 shadow-sm px-5 py-8 text-center text-sm text-gray-400">
              Нет активных разделов
            </div>
          ) : (
            <div className="rounded-xl bg-white border border-gray-200 shadow-sm overflow-hidden">
              <table className="min-w-full divide-y divide-gray-100 text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Раздел</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Проект</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Статус</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Начало</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-500">Срок</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {assigned_sections.map((s) => {
                    const isOverdue =
                      new Date(s.planned_end) < new Date() && s.status !== 'accepted'
                    return (
                      <tr
                        key={s.id}
                        className={`cursor-pointer transition-colors ${
                          isOverdue ? 'bg-rose-50 hover:bg-rose-100' : 'hover:bg-gray-50'
                        }`}
                        onClick={() => navigate(`/projects/${s.project_id}`)}
                      >
                        <td className="px-4 py-3 font-medium">{s.code}</td>
                        <td className="px-4 py-3 text-gray-600">
                          <span className="font-mono text-xs text-gray-400 mr-1.5">{s.project_code}</span>
                          {s.project_name}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                              STATUS_COLOR[s.status as SectionStatus]
                            }`}
                          >
                            {STATUS_LABELS[s.status as SectionStatus] ?? s.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-500 text-xs">{fmtDate(s.planned_start)}</td>
                        <td
                          className={`px-4 py-3 text-xs font-medium ${
                            isOverdue ? 'text-rose-600' : 'text-gray-600'
                          }`}
                        >
                          {fmtDate(s.planned_end)}
                          {isOverdue && <span className="ml-1 text-rose-400">⚠</span>}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Если директор/зам — показываем заглушку */}
      {!isGip && !isDesigner && (
        <div className="rounded-xl bg-white border border-gray-200 shadow-sm px-5 py-8 text-center text-sm text-gray-400">
          Для этой роли детальная нагрузка не отображается
        </div>
      )}
    </div>
  )
}
